import { useState, useEffect, useRef } from 'react';
import { apiService, PlayerSuggestionDTO } from '@/services/api.service';
import { formatPlayerName, playerLookupName } from '@/utils/playerName';
import styles from './PlayerAutocomplete.module.scss';

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Appelé quand une suggestion est choisie (clic / Entrée / clavier). */
  onSelect?: (s: PlayerSuggestionDTO) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Classe appliquée à l'input (pour matcher le style de la page hôte). */
  inputClassName?: string;
  /** Classe appliquée au conteneur (sizing flex de la page hôte). */
  className?: string;
  limit?: number;
  /**
   * Suggestions de pseudos (GET /api/players/suggest, lu dans la base stats du satellite). false :
   * simple champ texte, sans aucune requête, pour un satellite sans base (fiche joueur allégée).
   */
  suggest?: boolean;
}

/**
 * Champ texte avec recherche dynamique de joueurs (joueurs uniques du roster), par pseudo Recube ou
 * gamertag. Une suggestion s'affiche « pseudo (gamertag) » ; choisie, le champ prend son pseudo Recube
 * (unique, cherché en premier par le satellite), sinon son gamertag.
 * Debounce 200 ms, navigation clavier, fermeture au clic extérieur.
 * Réutilisé par la barre de recherche Joueurs et la modale de sanction.
 */
export function PlayerAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  autoFocus,
  disabled,
  inputClassName,
  className,
  limit = 8,
  suggest = true,
}: Props) {
  const [suggestions, setSuggestions] = useState<PlayerSuggestionDTO[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqId = useRef(0);
  // Pseudo tout juste sélectionné : on ne relance pas de recherche dessus.
  // Garde par comparaison de valeur (et non booléen) pour ne jamais avaler une frappe.
  const lastPicked = useRef<string | null>(null);

  useEffect(() => {
    const q = value.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Valeur inchangée depuis une sélection : ne pas rouvrir le dropdown.
    if (lastPicked.current !== null && q === lastPicked.current) {
      setOpen(false);
      return;
    }
    lastPicked.current = null;

    if (!suggest || q.length < 1) {
      // Suggestions coupées : une requête encore en vol ne rouvre pas la liste.
      if (!suggest) reqId.current++;
      setSuggestions([]);
      setOpen(false);
      setActive(-1);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const id = ++reqId.current;
      void apiService.suggestPlayers(q, limit).then((res) => {
        // Ignore les réponses obsolètes (course réseau)
        if (id !== reqId.current) return;
        const list = res.data ?? [];
        setSuggestions(list);
        setOpen(list.length > 0);
        setActive(-1);
      });
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, limit, suggest]);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const pick = (s: PlayerSuggestionDTO) => {
    const picked = playerLookupName(s.name, s.displayName);
    lastPicked.current = picked;
    if (debounceRef.current) clearTimeout(debounceRef.current); // annule un debounce en attente
    reqId.current++; // invalide une requête en vol
    onChange(picked);
    setSuggestions([]);
    setOpen(false);
    setActive(-1);
    onSelect?.(s);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Dropdown fermé : on laisse le comportement natif (ex. submit du formulaire hôte).
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      // Dropdown ouvert : Entrée choisit l'item actif, sinon le premier.
      // Toujours preventDefault pour ne pas soumettre le formulaire hôte pendant l'affichage des suggestions.
      e.preventDefault();
      pick(active >= 0 && active < suggestions.length ? suggestions[active]! : suggestions[0]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className={`${styles.box} ${className ?? ''}`} ref={boxRef}>
      <input
        type="text"
        className={inputClassName ?? styles.input}
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {open && suggestions.length > 0 && (
        <ul className={styles.suggestions} onMouseLeave={() => setActive(-1)}>
          {suggestions.map((s, idx) => (
            <li
              key={s.uuid}
              className={`${styles.item} ${idx === active ? styles.itemActive : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActive(idx)}
            >
              <img
                src={`https://mc-heads.net/avatar/${encodeURIComponent(s.name)}/24`}
                alt=""
                className={styles.avatar}
              />
              <span className={styles.name}>{formatPlayerName(s.name, s.displayName)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
